import React, { useEffect, useRef, useState } from 'react'
import { reportGio } from '../../../../../utils/gioReport'
import styles from './index.module.scss'

interface VideoPlayerProps {
  src: string;
  onClose: () => void;
}

const formatTime = (sec: number) => {
  if (!sec || !isFinite(sec)) return '00:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // 最新播放进度（秒），用于上报，避免闭包拿到旧 state
  const currentTimeRef = useRef(0);
  // 防止「播放结束」和「退出」重复上报
  const reportedRef = useRef(false);

  // 视频播放结束/退出时上报播放时长（GrowingIO/鼹鼠）
  const reportVideoPlay = () => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    const seconds = Math.round(currentTimeRef.current);
    reportGio({
      eventId: 'APP_P20383_B004',
      type: 'VIEW',
      name: '查看说明视频播放',
      playtime: String(seconds),
    });
  };

  const handleExit = () => {
    reportVideoPlay();
    onClose();
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  // 进入全屏：iOS 走视频原生全屏(webkitEnterFullscreen)，其余走容器 requestFullscreen。
  // 均不使用原生 controls，故不会出现下载按钮。
  const handleFullscreen = () => {
    const video = videoRef.current as any;
    if (!video) return;
    const container = video.parentElement;
    if (typeof video.webkitEnterFullscreen === 'function') {
      video.webkitEnterFullscreen();
    } else if (container && container.requestFullscreen) {
      container.requestFullscreen();
    } else if (container && container.webkitRequestFullscreen) {
      container.webkitRequestFullscreen();
    } else if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const time = Number(e.target.value);
    video.currentTime = time;
    setCurrentTime(time);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTimeUpdate = () => {
      currentTimeRef.current = video.currentTime;
      setCurrentTime(video.currentTime);
    };
    const onLoadedMetadata = () => setDuration(video.duration);
    const onEnded = () => reportVideoPlay();
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
      // 兜底：任何方式卸载播放器（如弹窗关闭）都视为退出播放，上报一次
      reportVideoPlay();
    };
  }, []);

  return (
    <div className={styles.videoOverlay} onClick={handleExit}>
      <div className={styles.videoPlayer} onClick={(e) => e.stopPropagation()}>
        <video
          ref={(el) => {
            (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
            if (el) {
              el.setAttribute('referrerpolicy', 'no-referrer');
              el.setAttribute('webkit-playsinline', 'true');
              el.setAttribute('x5-playsinline', 'true');
              el.setAttribute('x5-video-player-type', 'h5');
              el.setAttribute('x5-video-player-fullscreen', 'true');
            }
          }}
          src={src}
          autoPlay
          playsInline
          className={styles.videoFull}
          onClick={togglePlay}
        />

        {/* 中间播放/暂停按钮（暂停时显示） */}
        {!playing && (
          <div className={styles.centerPlay} onClick={togglePlay}>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="rgba(255,255,255,0.8)" />
              <path d="M26 20L46 32L26 44V20Z" fill="#333" />
            </svg>
          </div>
        )}

        {/* 自绘底部控制条 */}
        <div className={styles.controlBar} onClick={(e) => e.stopPropagation()}>
          <div className={styles.controlBtn} onClick={togglePlay}>
            {playing ? (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="5" y="4" width="4" height="14" rx="1" fill="#fff" />
                <rect x="13" y="4" width="4" height="14" rx="1" fill="#fff" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M6 4L18 11L6 18V4Z" fill="#fff" />
              </svg>
            )}
          </div>
          <span className={styles.timeText}>{formatTime(currentTime)}</span>
          <input
            className={styles.progress}
            type="range"
            min={0}
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
          />
          <span className={styles.timeText}>{formatTime(duration)}</span>
          <div className={styles.controlBtn} onClick={handleFullscreen}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M3 8V3H8M14 3H19V8M19 14V19H14M8 19H3V14" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <div className={styles.videoClose} onClick={handleExit}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="14" fill="rgba(0,0,0,0.5)" />
            <path d="M9 9L19 19M19 9L9 19" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
